import { CommonModule } from '@angular/common';
import { Component, HostListener, OnDestroy, OnInit, ViewChild, inject, ChangeDetectorRef } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import _ from 'lodash';
import { InfiniteScrollDirective } from 'ngx-infinite-scroll';
import { BadgeModule } from 'primeng/badge';
import { ButtonModule } from 'primeng/button';
import { ChipModule } from 'primeng/chip';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TabsModule } from 'primeng/tabs';
import { TooltipModule } from 'primeng/tooltip';
import { Subscription } from 'rxjs';
import { ToggleComponent } from 'src/app/@core/components/ui/toggle/toggle.component';
import { UserService } from 'src/app/contract-management/services/user.service';
import { ButtonComponent } from '../../../@core/components/ui/button/button.component';
import { IconComponent } from '../../../@core/components/ui/icon/icon.component';
import { PopoverComponent } from '../../../@core/components/ui/popover/popover.component';
import { JOB_STATUS, JOB_TYPE } from '../../../@core/models/enums';
import { IUser } from '../../../@core/models/model';
import { AuthenticationService } from '../../../@core/services/authentication.service';
import { DialogService } from '../../../@core/services/dialog.service';
import { NotificationActionHandlerService } from '../../../@core/services/notification-action-handler.service';
import { SOCKET_EVENTS, SocketService } from '../../../@core/services/socket.service';
import { ToastService } from '../../../@core/services/toast.service';
import { TextCasePipe } from '../../pipes/textCase.pipe';
import { TruncatePipe } from "../../pipes/truncate.pipe";
import { IconBadgeComponent } from '../icon-badge/icon-badge.component';
import { IMailMeta, INotification, INotificationMeta, INotificationType, NotificationActionType, NotificationService } from 'src/app/contract-management/services/notification.service';
import { IJob, JobService } from 'src/app/contract-management/services/job.service';
import { UserAvatarPipe } from '../../pipes/user-avatar-pipe';
import { FullNamePipe } from '../../pipes/fullName.pipe';
import { DateFormatterPipe } from '../../pipes/dateFormater.pipe';
import { DurationPipe } from '../../pipes/duration.pipe';

export enum JOB_FILTER_STATUS {
  STARTED = JOB_STATUS.STARTED,
  COMPLETED = JOB_STATUS.COMPLETED,
  FAILED = JOB_STATUS.FAILED,
  ALL = 'ALL'
}
@Component({
  selector: 'app-notifications',
  standalone: true,
  imports: [CommonModule, IconComponent, ButtonComponent, IconBadgeComponent, ButtonModule, PopoverComponent, UserAvatarPipe, BadgeModule, FullNamePipe, DateFormatterPipe, TabsModule, InfiniteScrollDirective, ProgressSpinnerModule, ChipModule, DurationPipe, TruncatePipe, TooltipModule, TextCasePipe, ToggleComponent, FormsModule],
  providers: [],
  templateUrl: './notifications.component.html',
  styleUrl: './notifications.component.scss'
})
export class NotificationsComponent implements OnInit, OnDestroy {
  @ViewChild('notificationPopover') notificationPopover!: PopoverComponent;
  notifications: INotification[] = []
  jobs: IJob[] = []
  jobFilters: { selected: JOB_FILTER_STATUS, skip: number, limit: number } = { selected: JOB_FILTER_STATUS.STARTED, skip: 0, limit: 20 }
  notificationFilters: { skip: number, limit: number } = { skip: 0, limit: 20 }
  jobLoading: boolean = false;
  notificationLoading: boolean = false;
  currentUser!: IUser;
  notificationsCount: number = 0;
  jobsCount: number = 0;

  get totalCount(): number {
    return this.notificationsCount;
  }
  notificationSubscription?: Subscription
  /** Tracks subscription to currentUser observable for cleanup */
  private userSubscription?: Subscription;
  JOB_FILTER_STATUS = JOB_FILTER_STATUS
  JOB_STATUS = JOB_STATUS
  activeTabIndex: number = 0;
  activeJobMenu: string | null = null;

  // notification settings
  showEmailNotifications: boolean = true;

  private readonly dialogService = inject(DialogService);

  constructor(
    private notificationService: NotificationService,
    private jobService: JobService,
    private authService: AuthenticationService,
    private router: Router,
    private socketService: SocketService,
    private toastService: ToastService,
    private userService: UserService,
    private notificationActionHandlerService: NotificationActionHandlerService
  ) {
    // Snapshot at construction time; reactively updated in ngOnInit via currentUser subscription
    this.currentUser = this.authService.getUser() ?? {} as IUser;
    if (!this.currentUser?.anonymous) {
      this.notificationSubscription = this.socketService.on<{ notification: INotification }>(SOCKET_EVENTS.NOTIFICATION).subscribe(res => {
        const notification = res[0].notification
        this.notifications.unshift(notification);
        this.notificationsCount += 1;
      })
    }
  }

  ngOnInit() {
    // Subscribe reactively so that if the anonymous token is generated after component init
    // (e.g. guest accessing a shared document), we correctly skip notification fetches.
    // We track whether data was already initialised to avoid duplicate fetches on re-emissions.
    let dataInitialised = false;

    this.userSubscription = this.authService.currentUser.subscribe(user => {
      this.currentUser = user ?? {} as IUser;

      if (this.currentUser?.anonymous) {
        // Clear any stale data for anonymous / guest users and skip API calls
        this.notifications = [];
        this.jobs = [];
        this.notificationsCount = 0;
        dataInitialised = false;
        return;
      }

      if (!user) return;

      // Only perform the initial data load once per authenticated session
      if (dataInitialised) return;
      dataInitialised = true;

      this.jobFilters.skip = 0;
      this.jobs = [];
      this.fetchJobsData(this.jobFilters, true);

      this.notificationFilters.skip = 0;
      this.notifications = [];
      this.fetchNotificationsData(this.notificationFilters, true);
      this.fetchUserSettings();
    });
  }

  ngOnDestroy(): void {
    if (this.notificationSubscription)
      this.notificationSubscription.unsubscribe()
    if (this.userSubscription)
      this.userSubscription.unsubscribe();
  }

  open(event: any) {
    this.jobFilters.skip = 0;
    this.jobs = [];
    this.fetchJobsData(this.jobFilters);

    this.notificationFilters.skip = 0;
    this.notifications = [];
    this.fetchNotificationsData(this.notificationFilters);
  }

  closePanel() {
    this.notificationPopover?.hide();
  }

  fetchUserSettings() {
    this.userService.getUserSettings().subscribe((res) => {
      if (res && res.success && res.data) {
        this.showEmailNotifications = res.data.notificationTypes.includes(INotificationType.EMAIL);
      }
    })
  }

  markAllAsRead() {
    this.notificationService.markAllAsRead().subscribe(() => {
      this.fetchNotificationsCount();
      this.notificationFilters.skip = 0;
      this.notifications = [];
      this.fetchNotificationsData(this.notificationFilters);
    })
  }

  fetchNotifications() {
    this.notificationService.getNotifications().subscribe((res) => {
      this.notifications = res.data.slice(0, 5)
      this.fetchNotificationsCount()
    })
  }

  fetchNotificationsData = _.debounce((notificationFilters: { skip: number, limit: number }, fetchNotificationsCount = false) => {
    this.notificationLoading = true
    this.notificationService.getNotifications(notificationFilters).subscribe((res) => {
      if (res.success && res.data) {
        this.notifications.push(...(res.data ?? []))
        this.notificationLoading = false
        if (fetchNotificationsCount) {
          this.fetchNotificationsCount();
        }
      }
      else
        this.notificationLoading = false
    })
  }, 150);

  fetchNotificationsCount() {
    this.notificationService.getNotificationsCount().subscribe((res) => {
      this.notificationsCount = res.data
    })
  }

  fetchJobsData = _.debounce((jobFilters: { selected: JOB_FILTER_STATUS, skip: number, limit: number }, fetchNotifications = false) => {
    this.jobLoading = true
    this.jobService.getJobs(jobFilters).subscribe((res) => {
      if (res.success && res.data) {
        this.jobs.push(...(res.data ?? []))
        this.jobLoading = false
        this.jobsCount = this.jobs.map(job => job.status).filter(status => status == JOB_STATUS.STARTED).length
        if (fetchNotifications) {
          this.fetchNotifications();
        }
      }
      else
        this.jobLoading = false
    })
  }, 150);

  onScroll() {
    if (this.jobLoading) {
      return;
    }
    this.jobFilters.skip += this.jobFilters.limit;
    this.fetchJobsData(this.jobFilters);
  }

  onNotificationScroll() {
    if (this.notificationLoading) {
      return;
    }
    this.notificationFilters.skip += this.notificationFilters.limit;
    this.fetchNotificationsData(this.notificationFilters);
  }

  chipClick(status: JOB_FILTER_STATUS) {
    if (this.jobLoading) {
      return
    }
    this.jobFilters.selected = status
    this.jobFilters.skip = 0;
    this.jobs = [];
    this.fetchJobsData(this.jobFilters)
  }

  jobText(job: IJob, title: boolean = false) {
    let statusText = '';
    switch (job.type) {
      case JOB_TYPE.REFRESH:
        statusText = 'Generating Insights';
        break;
      case JOB_TYPE.TAG_EXTRACTION:
        statusText = 'Tags Extraction';
        break;
      case JOB_TYPE.SINGLE_TAG_EXTRACTION:
        statusText = 'Single Tag Extraction';
        break;
      case JOB_TYPE.CHECKLIST:
        statusText = 'Checklist Compliance';
        break;
      case JOB_TYPE.SINGLE_CHECKLIST_ITEM:
        statusText = 'Single Checklist Compliance';
        break;
      case JOB_TYPE.SUMMARY:
        statusText = 'Summary Generation';
        break;
      case JOB_TYPE.REPO_TAG_EXTRACTION:
        statusText = 'Repository Tags Extraction';
        break;
      case JOB_TYPE.GENERATE_DOCUMENT:
        statusText = 'Document Generation';
        break;
      case JOB_TYPE.ATTACHMENT_EMBEDDINGS_GENERATION:
        statusText = 'Attachment Embeddings Generation';
        break;
      case JOB_TYPE.GENERATE_TEMPLATE:
        statusText = 'Template Generation';
        break;
      case JOB_TYPE.GENERATE_ANALYSIS_EXCEL:
        statusText = 'Analysis Excel Generation';
        break;
      case JOB_TYPE.GEOTECHNICAL_ANALYSIS:
        statusText = 'Geotechnical Analysis';
        break;
      case JOB_TYPE.GEOTECHNICAL_FORMULA_DASHBOARD:
        statusText = 'Formula Dashboard Generation';
        break;
      case JOB_TYPE.GEOTECHNICAL_VALUE_ENGINEERING:
        statusText = 'Value Engineering';
        break;
      case JOB_TYPE.GEOTECHNICAL_SUMMARY:
        statusText = 'Geotechnical Summary';
        break;
      case JOB_TYPE.GEOTECHNICAL_ANALYSIS_SUMMARY:
        statusText = 'Geotechnical Analysis Summary';
        break;
      case JOB_TYPE.GEOTECHNICAL_VENDOR_ANALYSIS:
        statusText = 'Geotechnical Vendor Analysis';
        break;
      case JOB_TYPE.GEOTECHNICAL_AI_DASHBOARD:
        statusText = 'AI Dashboard Generation';
        break;
      case JOB_TYPE.CLAUSE_EXTRACTION_RETRY:
        statusText = 'Clause Extraction';
        break;
      default:
        return '';
    }
    if (title)
      return statusText
    const itemName = job.item?.name;
    switch (job.status) {
      case JOB_STATUS.STARTED:
        return `${statusText} started${job.type == JOB_TYPE.GENERATE_DOCUMENT ? '' : itemName ? ' for ' + itemName : ''}.`;
      case JOB_STATUS.COMPLETED:
        return `${statusText} completed successfully${itemName ? ' for ' + itemName : ''}.`;
      case JOB_STATUS.FAILED:
        return `${statusText} Failed${itemName ? ' for ' + itemName : ''}.`;
      case JOB_STATUS.CANCELLED:
        return `${statusText} has been cancelled${itemName ? ' for ' + itemName : ''}.`;
      default:
        return '';
    }
  }

  allowCancel(job: IJob) {
    return job.status == JOB_STATUS.STARTED
  }

  toggleJobMenu(job: IJob) {
    this.activeJobMenu = this.activeJobMenu === job._id ? null : job._id;
  }

  @HostListener('document:click')
  closeJobMenu() {
    this.activeJobMenu = null;
  }

  onCancel(job: IJob) {
    this.jobService.cancelJob(job._id).subscribe(res => {
      if (res.success && res.data) {
        this.toastService.success('Task cancelled successfully.')
        this.chipClick(this.jobFilters.selected)
      }
    })
  }

  isNotificationMeta(meta: INotificationMeta | IMailMeta): meta is INotificationMeta {
    return (meta as INotificationMeta).message !== undefined;
  }

  isMailMeta(meta: INotificationMeta | IMailMeta): meta is IMailMeta {
    return (meta as IMailMeta).subject !== undefined;
  }

  markRead(notification: INotification) {
    this.notificationService.markRead(notification._id).subscribe(() => {
      this.fetchNotificationsCount();
      this.notificationFilters.skip = 0;
      this.notifications = [];
      this.fetchNotificationsData(this.notificationFilters);
    })
  }

  isNotificationClickable(notification: INotification): boolean {
    const data = notification.event?.data as { contractId?: string; templateUrl?: string; link?: string; actionType?: string };
    return data?.contractId !== undefined || data?.templateUrl !== undefined || data?.link !== undefined;
  }

  hasNotificationActionButton(notification: INotification): boolean {
    const data = notification.event?.data as { actionType?: string };
    const actionType = data?.actionType;
    return actionType != null && Object.values(NotificationActionType).includes(actionType as NotificationActionType);
  }

  getNotificationActionLabel(notification: INotification): string {
    const data = notification.event?.data as { actionLabel?: string };
    const label = data?.actionLabel?.trim();
    return label || 'Action';
  }

  executeNotificationAction(notification: INotification) {
    this.notificationActionHandlerService.handle(notification);
    this.markRead(notification);
  }

  action(notification: INotification) {
    const data = notification.event?.data as { link?: string; templateUrl?: string; actionType?: string };

    this.markRead(notification);

    // Handle template URL navigation (for in-app template notifications)
    if (data?.templateUrl) {
      window.open(data.templateUrl, '_blank');
    }

    // Handle regular external links
    if (data?.link) {
      window.open(data.link, '_blank');
    }
  }

  jobAction(job: IJob) {
    if (job.type === JOB_TYPE.GENERATE_TEMPLATE) {
      const templateId = job.metadata?.templateId;
      if (templateId && job.status === JOB_STATUS.COMPLETED) {
        this.navigateToTemplate(templateId);
      }
      return;
    }

    const contractId = job.metadata?.contractId
    if (contractId && (job.type == JOB_TYPE.GENERATE_DOCUMENT ? job.status == JOB_STATUS.COMPLETED : true))
      this.navigate(contractId)
  }

  navigate(contractId: string) {
    this.router.navigate(['/edit-document'], { queryParams: { documentId: contractId } });
  }

  navigateToTemplate(templateId: string) {
    this.router.navigate(['/settings/library/template-library'], { queryParams: { templateId: templateId } });
  }

  onTabChange(event: any) {
    const index = event
    this.activeTabIndex = index;
  }

  onToggleEmailNotifications(event: boolean) {
    this.closePanel();

    if (event === false) {
      this.showEmailNotifications = true;
      this.dialogService.confirm({
        title: 'Turn off notifications?',
        description: "You'll <span class=\"text-orange-600\">stop</span> receiving updates about task completions, mentions, and important alerts in your email. You can turn them back on anytime.",
        confirmText: 'Turn off',
        cancelText: 'Cancel',
        confirmVariant: 'destructive',
      }).subscribe(confirmed => {
        if (confirmed) {
          this.userService.updateUserSettings({ notificationTypes: [INotificationType.WEB] }).subscribe((res) => {
            if (res && res.success) {
              this.showEmailNotifications = false;
              this.toastService.success('Notification settings updated successfully.');
            } else {
              this.toastService.error('Failed to update notification settings.');
            }
          });
        }
      });
    } else {
      this.showEmailNotifications = false;
      this.dialogService.confirm({
        title: 'Turn on notifications?',
        description: "You'll <span class=\"text-orange-600\">start</span> receiving updates about task completions, mentions, and important alerts in your email. You can turn them back off anytime.",
        confirmText: 'Turn on',
        cancelText: 'Cancel',
      }).subscribe(confirmed => {
        if (confirmed) {
          this.userService.updateUserSettings({ notificationTypes: [INotificationType.WEB, INotificationType.EMAIL] }).subscribe((res) => {
            if (res && res.success) {
              this.showEmailNotifications = true;
              this.toastService.success('Notification settings updated successfully.');
            } else {
              this.toastService.error('Failed to update notification settings.');
            }
          });
        }
      });
    }
  }
}
